import ReactPixel from "react-facebook-pixel";
import PixelErrorHandler from "./pixelErrorHandler";
import { logInfo, logError, logSuccess } from "../utils/logger.js";

/**
 * Facebook Pixel Service
 * Centralized service for Facebook Pixel event tracking
 * Uses the existing pixel ID from index.html: 1330380002098667
 */
class FacebookPixelService {
  static get initialized() {
    return this._initialized || false;
  }

  static set initialized(value) {
    this._initialized = value;
  }

  static get pixelId() {
    return this._pixelId || "1330380002098667"; // From existing index.html
  }

  static set pixelId(value) {
    this._pixelId = value;
  }

  /**
   * Initialize Facebook Pixel
   * @returns {void}
   */
  static init() {
    if (!this.initialized) {
      try {
        ReactPixel.init(this.pixelId);
        this.initialized = true;
        logSuccess("Facebook Pixel initialized successfully");

        // Retry any failed events now that pixel is working
        PixelErrorHandler.retryFailedEvents((eventName, parameters) => {
          ReactPixel.track(eventName, parameters);
        });
      } catch (error) {
        const handled = PixelErrorHandler.handleInitError(error, () => {
          this.init();
        });

        if (!handled) {
          throw error;
        }
      }
    }
  }

  /**
   * Track a Facebook Pixel event
   * @param {string} eventName - Event name (ViewContent, Purchase, Lead, etc.)
   * @param {Object} parameters - Event parameters
   * @returns {void}
   */
  static track(eventName, parameters = {}) {
    try {
      this.init();
      ReactPixel.track(eventName, parameters);
      logInfo(`Facebook Pixel event tracked: ${eventName}`, parameters);
    } catch (error) {
      const handled = PixelErrorHandler.handleTrackError(
        error,
        eventName,
        parameters
      );

      if (!handled) {
        logError(`Facebook Pixel track failed for ${eventName}:`, error);
        throw error;
      }
    }
  }

  /**
   * Track quote view event
   * @param {string} quoteId - Quote ID
   * @param {number} estimatedCost - Quote estimated cost
   * @returns {void}
   */
  static trackQuoteView(quoteId, estimatedCost) {
    this.track("ViewContent", {
      content_type: "quote",
      content_ids: [quoteId],
      value: estimatedCost,
      currency: "ZAR",
    });
  }

  /**
   * Track quote acceptance event
   * @param {string} quoteId - Quote ID
   * @param {number} estimatedCost - Quote estimated cost
   * @returns {void}
   */
  static trackQuoteAccept(quoteId, estimatedCost) {
    this.track("Purchase", {
      content_type: "quote",
      content_ids: [quoteId],
      value: estimatedCost,
      currency: "ZAR",
    });
  }

  /**
   * Track lead event
   * @param {Object} campaignData - Campaign data
   * @returns {void}
   */
  static trackLead(campaignData) {
    this.track("Lead", {
      content_name: "WhatsApp Lead",
      content_category: "logistics_quote",
      value: campaignData.leadValue || 0,
      currency: "ZAR",
      custom_data: campaignData,
    });
  }

  /**
   * Track custom event
   * @param {string} eventName - Custom event name
   * @param {Object} parameters - Event parameters
   * @returns {void}
   */
  static trackCustomEvent(eventName, parameters = {}) {
    this.track("CustomEvent", {
      event_name: eventName,
      ...parameters,
    });
  }

  /**
   * Track quote sharing event
   * @param {string} quoteId - Quote ID
   * @param {string} shareMethod - How the quote was shared
   * @returns {void}
   */
  static trackQuoteShare(quoteId, shareMethod = "unknown") {
    this.trackCustomEvent("QuoteShared", {
      content_type: "quote",
      content_ids: [quoteId],
      share_method: shareMethod,
    });
  }

  /**
   * Track quote rejection event
   * @param {string} quoteId - Quote ID
   * @param {string} reason - Rejection reason
   * @returns {void}
   */
  static trackQuoteReject(quoteId, reason = "no_reason") {
    this.trackCustomEvent("QuoteRejected", {
      content_type: "quote",
      content_ids: [quoteId],
      rejection_reason: reason,
    });
  }

  /**
   * Check if pixel is initialized
   * @returns {boolean}
   */
  static isInitialized() {
    return this.initialized;
  }

  /**
   * Get pixel ID
   * @returns {string}
   */
  static getPixelId() {
    return this.pixelId;
  }
}

export default FacebookPixelService;
